// Live side-by-side preview of the homepage hero in every language — mirrors Hero.jsx fallback chain (lang → en → default).
import React, { useEffect, useState } from 'react';
import { Eye, MapPin } from 'lucide-react';
import { apiCall } from './_shared';

const resolve = (map, code, fallback) => {
  const own = (map[code] || '').trim();
  if (own) return { text: own, source: 'own' };
  const en = (map.en || '').trim();
  if (en) return { text: en, source: 'en' };
  return { text: fallback, source: 'default' };
};

const SOURCE_LABEL = { own: null, en: 'falls back to English', default: 'built-in default' };

const HeroCard = ({ lang, hotel, bg, tagline, description, active, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(lang.code)}
    className={`relative text-left rounded-xl overflow-hidden border-2 transition-all duration-200 group ${active ? 'border-orange-500 shadow-lg' : 'border-transparent hover:border-orange-300'}`}
    data-testid={`hero-preview-${lang.code}`}
  >
    <div className="relative aspect-[16/10] bg-slate-800">
      {bg && <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />
      <div className="absolute inset-0 p-3 flex flex-col justify-end text-white">
        <p className="flex items-center gap-1 text-[9px] text-gray-300 mb-1"><MapPin className="w-2.5 h-2.5" />{hotel.city}, {hotel.country}</p>
        <p className="text-sm font-bold leading-tight truncate">{hotel.name}</p>
        <p className="text-[11px] text-gray-200 font-light leading-snug line-clamp-2 mt-0.5" data-testid={`hero-preview-tagline-${lang.code}`}>{tagline.text || '—'}</p>
        <p className="text-[9px] text-gray-300 leading-snug line-clamp-3 mt-1" data-testid={`hero-preview-desc-${lang.code}`}>{description.text || '—'}</p>
      </div>
    </div>
    <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white text-[11px]">
      <span className="font-semibold text-gray-800"><span className="text-gray-400 mr-1">{lang.flag}</span>{lang.name}</span>
      {(SOURCE_LABEL[tagline.source] || SOURCE_LABEL[description.source]) && (
        <span className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 whitespace-nowrap" data-testid={`hero-preview-fallback-${lang.code}`}>
          {SOURCE_LABEL[description.source] || SOURCE_LABEL[tagline.source]}
        </span>
      )}
    </div>
  </button>
);

export const HeroPreview = ({ languages, taglines, descriptions, fallbackTagline, fallbackDescription, hotel, activeLang, onSelect, dirty }) => {
  const [bg, setBg] = useState('');
  useEffect(() => {
    apiCall('/api/gallery').then((g) => { if (Array.isArray(g) && g[0]?.url) setBg(g[0].url); }).catch(() => {});
  }, []);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="hero-preview">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Eye className="w-4 h-4 text-orange-500" /> Live hero preview — all languages</h3>
        <span className={`text-xs rounded-full px-2.5 py-1 ${dirty ? 'bg-orange-50 text-orange-700 border border-orange-200' : 'bg-green-50 text-green-700 border border-green-200'}`} data-testid="hero-preview-state">
          {dirty ? 'Showing unsaved edits — press Save All to publish' : 'Matches what visitors see now'}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {languages.map((l) => (
          <HeroCard
            key={l.code}
            lang={l}
            hotel={hotel}
            bg={bg}
            tagline={resolve(taglines, l.code, fallbackTagline)}
            description={resolve(descriptions, l.code, fallbackDescription)}
            active={activeLang === l.code}
            onSelect={onSelect}
          />
        ))}
      </div>
      <p className="text-[11px] text-gray-500 mt-3">Click a card to edit that language. Amber badge = that language is empty and visitors will see the English (or built-in) text instead.</p>
    </div>
  );
};

export default HeroPreview;
